import { HardHat, ShieldCheck, UserCog } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/shared/lib/utils';
import { useFieldRole, type FieldRole } from '../../role';

const OPTIONS: Array<{ role: FieldRole; label: string; icon: LucideIcon }> = [
  { role: 'worker', label: 'Field Staff', icon: HardHat },
  { role: 'supervisor', label: 'Supervisor/Lead', icon: UserCog },
  { role: 'compliance', label: 'Compliance', icon: ShieldCheck },
];

/**
 * Persona preview switcher (prototype tool).
 *
 * The field app serves three personas off one model. With no backend to
 * derive the live role from, this segmented control lets the demo flip
 * between Worker / Supervisor (crew lead) / Compliance. Persisted via the
 * role store so the choice survives reloads. When the backend lands, the
 * available options collapse to what the user is actually entitled to
 * (see `deriveAvailableRoles`).
 */
export function FieldRoleSwitcher({ onChange }: { onChange?: () => void }) {
  const { role, setRole } = useFieldRole();

  return (
    <div className="tw-rounded-xl tw-border tw-bg-muted/30 tw-p-3">
      <p className="tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
        Preview as
      </p>
      <div className="tw-grid tw-grid-cols-3 tw-gap-1.5">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = role === opt.role;
          return (
            <button
              key={opt.role}
              type="button"
              aria-pressed={active}
              onClick={() => {
                setRole(opt.role);
                onChange?.();
              }}
              className={cn(
                'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-border tw-px-2 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-transition-all',
                active
                  ? 'tw-border-primary tw-bg-primary tw-text-primary-foreground tw-shadow-sm'
                  : 'tw-border-transparent tw-bg-background tw-text-muted-foreground hover:tw-bg-accent hover:tw-text-foreground',
              )}
            >
              <Icon className="tw-h-4 tw-w-4 tw-shrink-0" />
              <span className="tw-text-center tw-leading-tight">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
